import { DateTime } from "luxon"
import { IconDefinition } from "@fortawesome/pro-light-svg-icons"
import { MonetaryAmount } from "../../../../currency/currencyTypes"
import { BuyerParty } from "../../../../payments/orderTypes"
import { PaymentMethodType } from "../../../../payments/paymentTypes"
import { OrderStatus } from "../../../controlKitchenTypes"
import { OrderSummary } from "../../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"
import { TranslationKey } from "../../../../localization/translations"
import { OrganizerType } from "../../../../../shared/types/OrganizerTypes"
import { ChatMessage } from "../../../../../shared/components/orderChat/orderChatTypes"
import { DateTimeFactory } from "../../../../localization/useDateTime"

export enum TimePeriodMeetingService {
    ALL = "ALL",
    TODAY = "TODAY",
    YESTERDAY = "YESTERDAY",
    TOMORROW = "TOMORROW",
    THIS_WEEK = "THIS_WEEK",
    THIS_MONTH = "THIS_MONTH",
}

export enum DeliveryType {
    DELIVERY = "DELIVERY",
    PICK_UP = "PICK_UP",
    EATING_IN_CANTEEN = "EATING_IN_CANTEEN",
}

export enum DeliveryTypeFilter {
    ALL = "ALL",
    DELIVERY = "DELIVERY",
    PICK_UP = "PICK_UP",
    EATING_IN_CANTEEN = "EATING_IN_CANTEEN",
}

export const DELIVERY_FILTER_ALL = "ALL"

export type DeliveryFilter = DeliveryType | typeof DELIVERY_FILTER_ALL

export type DeliveryFilterValue = DeliveryFilter

export type GroupBy = "location" | "time"

type AccountingDetails = Readonly<{
    bookedTime: string
    accountingCostCode: string
    costCodes: Array<{
        name: string
        value: string
    }>
    isBooked: boolean
    isConfirmed: boolean
    isLocked: boolean
}>

export type OrderStatusDetails = {
    status: FilteredStatuses
    message: string
}

type DeliveryDetails = Readonly<{
    time: string
    location: {
        name: string
    }
    type: DeliveryType
    sittings: number
    isLocked?: boolean
    statusDetails: OrderStatusDetails
}>

type PartyDetails = Readonly<{
    uid: string
    name: string
    phoneNumber: string
    email: string
    streetName: string
    streetNumber: string
    postalCode: string
    city: string
    contactPerson: string
    vatNumber?: string
    id?: number
}>

type MeetingDetails = Readonly<{
    meetingName: string
    location: {
        name: string
    }
    startDateTime: string
    endDateTime: string
    attendees: Array<{
        company: string
        name: string
        email: string
    }>
}>

type PaymentDetails = Readonly<{
    paymentMehod: PaymentMethodType
    salesChannel: "GoPay app"
    type: "DEBIT"
    isContantPayment: boolean
}>

type CustomerDetails = Readonly<{
    id: number
    email: string
    employeeId: string
    displayName: string
    initials: string
    department: {
        name: string
        number: string
    }
    userGroup?: {
        id: number
        name: string
    }
}>

export type OrganizerDetails = Readonly<{
    name: string
    email: string
    type: OrganizerType
    // Api should be updated
    company?: {
        name: string
    }
}>

export enum DetailsOrderType {
    MEETING_SERVICE = "MEETING_SERVICE",
}

export type AllowStatusTransition = {
    [K in MeetingServiceOrderStatus]: string
}

export type OrderActionDetails = Readonly<{
    allow: boolean
    message?: {
        title: string
        body: string
    }
}>

export interface OrderAction {
    statusTransitions: {
        disallowed?: AllowStatusTransition
        allowed?: AllowStatusTransition
    }
    refundPOSOrder?: OrderActionDetails
}

export type MeetingServiceOrder = Readonly<{
    id: number
    uid: string
    lineNumber: number
    deliveries: Array<{
        id: number
        deliveryType: string
        deliveryTime: string
        orderNote: string
        price: MonetaryAmount
        orderLines: Array<{
            id: number
            items: number
            itemPrice: MonetaryAmount
            price: MonetaryAmount
            vat: number
            productId: number
            name: string
            buyerParty: BuyerParty
            accountNumber: string
            productNumber: string
        }>
    }>
    orderSummary: OrderSummary
    amount: MonetaryAmount
    accountingDetails: AccountingDetails
    deliveryDetails: DeliveryDetails
    accountingSupplierParty: PartyDetails
    sellerSupplierParty: PartyDetails
    buyerCustomerParty: PartyDetails
    accountingCustomerParty: PartyDetails
    meetingDetails: MeetingDetails
    paymentDetails: PaymentDetails
    orderNote: string
    orderType: DetailsOrderType
    orderStatus: OrderStatus
    permaLink: string
    created: string
    updated: string
    customer: CustomerDetails
    organizers: Array<OrganizerDetails>
    orderAction: OrderAction
    messages?: ChatMessage[]
}>

export function getDeliveryTypeFilterKey(filter: DeliveryFilterValue): string {
    return filter === DELIVERY_FILTER_ALL ? DeliveryTypeFilter.ALL : filter
}

export function getDeliveryFilterFromKey(key: string | undefined): DeliveryFilterValue {
    if (!key || key === DeliveryTypeFilter.ALL) return DELIVERY_FILTER_ALL
    return key as DeliveryType
}
interface TimePeriodResult {
    start: DateTime
    end: DateTime
}
export const getTimePeriod = (
    dateTimeFactory: DateTimeFactory,
    period: TimePeriodMeetingService,
): TimePeriodResult | null => {
    const now = dateTimeFactory.now()
    switch (period) {
        case TimePeriodMeetingService.ALL:
            return null
        case TimePeriodMeetingService.YESTERDAY:
            return {
                start: now.minus({ days: 1 }).startOf("day"),
                end: now.minus({ days: 1 }).endOf("day"),
            }
        case TimePeriodMeetingService.TOMORROW:
            return {
                start: now.plus({ days: 1 }).startOf("day"),
                end: now.plus({ days: 1 }).endOf("day"),
            }
        case TimePeriodMeetingService.THIS_WEEK:
            return {
                start: now.startOf("week"),
                end: now.endOf("week"),
            }
        case TimePeriodMeetingService.THIS_MONTH:
            return {
                start: now.startOf("month"),
                end: now.endOf("month"),
            }
        default:
            return {
                start: now.startOf("day"),
                end: now.endOf("day"),
            }
    }
}

export enum MeetingServiceOrdersView {
    NOT_APPROVED = "NOT_APPROVED",
    TO_BE_BOOKED = "TO_BE_BOOKED",
    PRODUCTION = "PRODUCTION",
}

export enum MeetingServiceOrderStatus {
    NOT_APPROVED = "NOT_APPROVED",
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
    LOCKED = "LOCKED",
    PREPARING = "PREPARING",
    DELIVERED = "DELIVERED",
    TO_BE_BOOKED = "TO_BE_BOOKED",
    BOOKED = "BOOKED",
    DEFAULT = "DEFAULT",
    READY = "READY",
    COMPLETED = "COMPLETED",
}

export enum DashboardNotifications {
    PRODUCTION_MEETING_SERVICE_ORDERS = "PRODUCTION_MEETING_SERVICE_ORDERS",
    TODAYS_MEETING_SERVICE_ORDERS = "TODAYS_MEETING_SERVICE_ORDERS",
    TODAYS_TAKE_AWAY_ORDERS = "TODAYS_TAKE_AWAY_ORDERS",
    TO_BE_BOOKED = "TO_BE_BOOKED",
    NOT_APPROVED = "NOT_APPROVED",
}

export type FilteredStatuses = Exclude<MeetingServiceOrderStatus, MeetingServiceOrderStatus.TO_BE_BOOKED>

export const AVAILABLE_STATUSES: FilteredStatuses[] = [
    MeetingServiceOrderStatus.NOT_APPROVED,
    MeetingServiceOrderStatus.APPROVED,
    MeetingServiceOrderStatus.REJECTED,
    MeetingServiceOrderStatus.PREPARING,
    MeetingServiceOrderStatus.DELIVERED,
    MeetingServiceOrderStatus.BOOKED,
    MeetingServiceOrderStatus.READY,
    MeetingServiceOrderStatus.COMPLETED,
]

export interface DeliveryStatusInfo {
    label: string
    disabled?: boolean
    statusItemLabel: string
    icon: IconDefinition
    bgColor: string
    textColor: string
    actionTranslationId: TranslationKey
    stateTranslationId: TranslationKey
    borderColor: string
}

export interface StatusDetails {
    status: FilteredStatuses
    reason?: string
    message: string
}

export interface DeliveryBatchDetails {
    statusDetails: StatusDetails
}

export interface Order {
    id: number
    deliveryDetails: DeliveryBatchDetails
}

export interface TransactionStatus {
    code: number
    text: string
}

export interface TransactionResult {
    order: Order
    status: TransactionStatus
}

export interface TransactionResult {
    order: Order
    status: TransactionStatus
}

export interface TransactionLockResult extends Order {
    status: TransactionStatus
}
